import { BaseApi } from "../utils/request";
import type {
  TrackingEventRequest,
  TrackingEventResponse,
} from "../types/tracking.types";
import type { LandingAttributionData } from "../types/landingAttribution.types";

/**
 * 使用 sendBeacon 发送数据，不占用 fetch 连接池
 * 失败时回退到普通 POST
 */
function trySendBeacon(url: string, data: unknown): boolean {
  if (navigator.sendBeacon) {
    try {
      const blob = new Blob([JSON.stringify(data)], {
        type: "application/json",
      });
      return navigator.sendBeacon(url, blob);
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Tracking API 类
 * 提供归因追踪相关的 API 接口
 * 优先使用 sendBeacon 发送，避免占用主线程网络连接
 */
class TrackingApi extends BaseApi {
  constructor() {
    super();
  }

  /**
   * 上报落地页浏览器归因数据
   */
  async trackLandingAttribution(
    data: LandingAttributionData
  ): Promise<TrackingEventResponse> {
    try {
      // 优先用 sendBeacon，不阻塞页面、不占连接
      if (trySendBeacon("/api/tracking/landingads", data)) {
        return { success: true };
      }
      const response = await this.post<TrackingEventResponse>(
        "/api/tracking/landingads",
        data
      );
      return response?.data || { success: true };
    } catch (error) {
      console.error("落地页归因上报失败:", error);
      return { success: false, message: String(error) };
    }
  }

  /**
   * 上报埋点事件
   * @param data 埋点事件数据
   */
  async trackEvent(data: TrackingEventRequest): Promise<TrackingEventResponse> {
    try {
      // 优先用 sendBeacon，不阻塞页面、不占连接
      if (trySendBeacon("/api/tracking/event", data)) {
        return { success: true };
      }
      const response = await this.post<TrackingEventResponse>(
        "/api/tracking/event",
        data
      );
      return response?.data || { success: true };
    } catch (error) {
      console.error("埋点上报失败:", error);
      return { success: false, message: String(error) };
    }
  }
}

// 创建单例实例
const trackingApi = new TrackingApi();

/**
 * 上报下载 APK 追踪数据
 * @param data 追踪数据（可选）
 */
// export const landingDownloadApk = (data?: TrackingData) =>
//   trackingApi.landingDownloadApk(data);

/**
 * 上报埋点事件
 * @param data 埋点事件数据
 */
export const trackEvent = (data: TrackingEventRequest) =>
  trackingApi.trackEvent(data);

/**
 * 上报落地页浏览器归因数据
 */
export const landingAttribution = (data: LandingAttributionData) =>
  trackingApi.trackLandingAttribution(data);

export { trackingApi };
